import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Compass, Check, LogOut, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const POPULAR_KEY = "pro";

export default function Subscribe() {
  const { logout } = useAuth();
  const { t } = useI18n();
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(null);

  const { data: plans = [] } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await api.get("/plans")).data,
  });

  const startCheckout = async (planKey) => {
    setBusy(planKey);
    try {
      const res = await api.post("/billing/checkout", { plan: planKey, interval });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("subscribe_checkout_unavailable"));
      setBusy(null);
    }
  };

  const bulletsFor = (p) => {
    const f = p.features || {};
    const out = [
      p.property_limit < 0 ? t("subscribe_unlimited") : `${p.property_limit} ${t("subscribe_properties")}`,
    ];
    if (f.ai) out.push(t("nav_finance_ai"));
    if (f.deal_analyzer) out.push(t("nav_deal_analyzer"));
    if (f.market) out.push(t("nav_market"));
    return out.slice(0, 5);
  };

  const annual = interval === "year";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="flex items-center justify-between px-6 h-16 border-b border-border">
        <div className="flex items-center gap-2">
          <Compass className="h-5 w-5 text-[hsl(var(--success))]" strokeWidth={2.2} />
          <span className="font-display text-xl">YieldPilot</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => logout()} data-testid="subscribe-logout"
          className="gap-2 text-muted-foreground">
          <LogOut className="h-4 w-4" /> {t("logout")}
        </Button>
      </header>

      <div className="flex-1 flex flex-col items-center px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-5xl text-center">
          <h1 className="font-display text-4xl mb-3">{t("subscribe_title")}</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">{t("subscribe_sub")}</p>

          {/* Monthly / Annual toggle */}
          <div className="inline-flex items-center gap-1 mt-8 p-1 rounded-full border border-border bg-secondary/40"
            data-testid="interval-toggle">
            {["month", "year"].map((iv) => (
              <button key={iv} onClick={() => setInterval(iv)} data-testid={`interval-${iv}`}
                className={`px-5 py-2 rounded-full text-sm transition-colors ${
                  interval === iv ? "bg-background shadow-sm font-medium" : "text-muted-foreground"
                }`}>
                {iv === "month" ? t("billing_interval_monthly") : t("billing_interval_annual")}
              </button>
            ))}
          </div>
          {annual && (
            <p className="text-xs text-[hsl(var(--success))] mt-2">{t("billing_save_annual")}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10 text-left">
            {plans.map((p) => {
              const price = annual ? p.price_yearly : p.price_monthly;
              const available = price != null;
              const savings = annual && p.price_yearly != null && p.price_monthly
                ? p.price_monthly * 12 - p.price_yearly : 0;
              const popular = p.key === POPULAR_KEY;
              return (
                <Card key={p.key} data-testid={`plan-card-${p.key}`}
                  className={`relative p-6 rounded-sm flex flex-col ${
                    popular ? "border-[hsl(var(--success))] shadow-lg" : "border-border"
                  }`}>
                  {popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-sm bg-[hsl(var(--success))] text-white">
                      {t("subscribe_popular")}
                    </Badge>
                  )}
                  <h3 className="font-display text-2xl">{p.name}</h3>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="font-display text-4xl tabular-nums">
                      {available ? fmtCurrency(price) : "—"}
                    </span>
                    <span className="text-muted-foreground mb-1 text-sm">
                      {annual ? t("billing_per_year") : t("billing_per_month")}
                    </span>
                  </div>
                  {annual && savings > 0 && (
                    <p className="text-xs text-[hsl(var(--success))] mt-1">
                      {t("billing_annual_savings")} {fmtCurrency(savings)}
                    </p>
                  )}
                  <ul className="mt-5 space-y-2 flex-1">
                    {bulletsFor(p).map((b, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-[hsl(var(--success))] shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => startCheckout(p.key)} disabled={!available || busy === p.key}
                    data-testid={`subscribe-${p.key}`}
                    variant={popular ? "default" : "outline"}
                    className="w-full rounded-sm mt-6">
                    {busy === p.key ? "..." : t("subscribe_start_trial")}
                  </Button>
                </Card>
              );
            })}
          </div>

          <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-8">
            <ShieldCheck className="h-4 w-4" /> {t("subscribe_reassure")}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
